import React, { useState, useEffect } from 'react';
import type { DiaryItem, Activity } from '../models/diary';
import { diaryService, ExistingActivity } from '../services/diaryService';

const FitnessItemEditForm: React.FC<{ item: DiaryItem; onChange: (field: string, value: any) => void }> = ({ item, onChange }) => {
  // State for existing items from database
  const [existingActivities, setExistingActivities] = useState<ExistingActivity[]>([]);
  const [loadingExistingItems, setLoadingExistingItems] = useState(false);
  
  // State for activity selection mode
  const [activitySelectionMode, setActivitySelectionMode] = useState<'existing' | 'new'>('existing');
  const [selectedExistingActivityIds, setSelectedExistingActivityIds] = useState<number[]>([]);

  // Fetch existing activities when component mounts
  useEffect(() => {
    const fetchExistingActivities = async () => {
      setLoadingExistingItems(true);
      try {
        const activities = await diaryService.getExistingActivities();
        setExistingActivities(activities);
        
        // Initialize selected existing activity IDs based on current activities
        const existingIds = (item.activities || [])
          .filter(activity => activity.id && activities.some(ea => ea.id === activity.id))
          .map(activity => activity.id!);
        setSelectedExistingActivityIds(existingIds);
      } catch (error) {
        console.error('Error fetching existing activities:', error);
      } finally {
        setLoadingExistingItems(false);
      }
    };

    fetchExistingActivities();
  }, [item.activities]);
  // Helper functions for activity management
  const handleActivityChange = (activityIndex: number, field: keyof Activity, value: string) => {
    const updatedActivities = [...(item.activities || [])];
    updatedActivities[activityIndex] = {
      ...updatedActivities[activityIndex],
      [field]: value
    };
    onChange('activities', updatedActivities);
  };

  const addActivity = () => {
    const newActivity: Activity = {
      id: Date.now(), // Temporary ID for new activities
      name: '',
      type: '',
      details: '',
      activity_level: ''
    };
    const updatedActivities = [...(item.activities || []), newActivity];
    onChange('activities', updatedActivities);
  };

  const removeActivity = (activityIndex: number) => {
    const updatedActivities = [...(item.activities || [])];
    updatedActivities.splice(activityIndex, 1);
    onChange('activities', updatedActivities);
  };

  // Helper functions for existing activity selection
  const handleExistingActivitySelection = (activityId: number) => {
    const existingActivity = existingActivities.find(a => a.id === activityId);
    if (!existingActivity) return;

    // Convert existing activity to Activity format
    const activity: Activity = {
      id: existingActivity.id,
      name: existingActivity.name,
      type: '', // Not used for existing activities
      details: existingActivity.details,
      activity_level: existingActivity.activity_level
    };

    const updatedActivities = [...(item.activities || []), activity];
    onChange('activities', updatedActivities);
    setSelectedExistingActivityIds(prev => [...prev, activityId]);
  };

  const removeExistingActivity = (activityId: number) => {
    const updatedActivities = (item.activities || []).filter(a => a.id !== activityId);
    onChange('activities', updatedActivities);
    setSelectedExistingActivityIds(prev => prev.filter(id => id !== activityId));
  };

  return (
  <>
    <label>Nombre
      <input value={item.name} onChange={e => onChange('name', e.target.value)} style={{ width: '100%', marginBottom: 10 }} />
    </label>
    <label>Día de la semana
      <select value={item.dayOfWeek} onChange={e => onChange('dayOfWeek', e.target.value)} style={{ width: '100%', marginBottom: 10 }}>
        <option value="Monday">Lunes</option>
        <option value="Tuesday">Martes</option>
        <option value="Wednesday">Miércoles</option>
        <option value="Thursday">Jueves</option>
        <option value="Friday">Viernes</option>
        <option value="Saturday">Sábado</option>
        <option value="Sunday">Domingo</option>
      </select>
    </label>
    <label>Momento del día
      <select value={item.timeOfDay} onChange={e => onChange('timeOfDay', e.target.value)} style={{ width: '100%', marginBottom: 10 }}>
        <option value="Morning">Mañana</option>
        <option value="Afternoon">Tarde</option>
        <option value="Evening">Noche</option>
        <option value="Other">Otro</option>
      </select>
    </label>
    <label>Tipo de ejercicio
      <select value={item.subtype} onChange={e => onChange('subtype', e.target.value)} style={{ width: '100%', marginBottom: 10 }}>
        <option value="cardio">Cardio</option>
        <option value="gym">Gym</option>
        <option value="sport">Sport</option>
        <option value="swimming">Swimming</option>
        <option value="yoga">Yoga</option>
        <option value="pilates">Pilates</option>
        <option value="walking">Walking</option>
      </select>
    </label>
    <label>Orden
      <input type="number" value={item.order} onChange={e => onChange('order', parseInt(e.target.value))} style={{ width: '100%', marginBottom: 10 }} min="1" />
    </label>
    <label style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
      <input type="checkbox" checked={item.hidden || false} onChange={e => onChange('hidden', e.target.checked)} style={{ marginRight: 8 }} />
      Oculto
    </label>

    {/* Activities Section */}
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontWeight: 600, margin: 0 }}>Actividades</label>
      </div>

      {/* Activity Selection Mode Toggle */}
      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <input 
            type="radio" 
            name="activityMode" 
            checked={activitySelectionMode === 'existing'} 
            onChange={() => setActivitySelectionMode('existing')}
            style={{ marginRight: 6 }}
          />
          <span style={{ fontSize: 14 }}>Seleccionar de existentes</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input 
            type="radio" 
            name="activityMode" 
            checked={activitySelectionMode === 'new'} 
            onChange={() => setActivitySelectionMode('new')}
            style={{ marginRight: 6 }}
          />
          <span style={{ fontSize: 14 }}>Crear nueva actividad</span>
        </label>
      </div>

      {/* Existing Activities Selection */}
      {activitySelectionMode === 'existing' && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Seleccionar actividad:</span>
            {loadingExistingItems ? (
              <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando actividades...</div>
            ) : (
              <select 
                onChange={e => e.target.value && handleExistingActivitySelection(parseInt(e.target.value))}
                style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }}
                value=""
              >
                <option value="">Seleccionar una actividad</option>
                {existingActivities
                  .filter(activity => !selectedExistingActivityIds.includes(activity.id))
                  .map(activity => (
                    <option key={activity.id} value={activity.id}>
                      {activity.name} ({activity.activity_level})
                    </option>
                  ))
                }
              </select>
            )}
          </label>
        </div>
      )}

      {/* New Activity Creation */}
      {activitySelectionMode === 'new' && (
        <div style={{ marginBottom: 10 }}>
          <button 
            type="button" 
            onClick={addActivity}
            style={{ 
              background: '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 4, 
              padding: '4px 8px', 
              fontSize: 12, 
              cursor: 'pointer',
              marginBottom: 8
            }}
          >
            + Agregar Nueva Actividad
          </button>
        </div>
      )}

      {/* Display Selected/Created Activities */}
      {(item.activities || []).map((activity, index) => {
        const isExisting = activity.id && selectedExistingActivityIds.includes(activity.id);
        return (
          <div key={activity.id || index} style={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 6, 
            padding: 8, 
            marginBottom: 8, 
            backgroundColor: isExisting ? '#f0f9ff' : '#f9f9f9' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {isExisting ? `Actividad Existente: ${activity.name}` : `Actividad ${index + 1}`}
              </span>
              <button 
                type="button" 
                onClick={() => isExisting ? removeExistingActivity(activity.id!) : removeActivity(index)}
                style={{ 
                  background: '#ef4444', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 4, 
                  padding: '2px 6px', 
                  fontSize: 12, 
                  cursor: 'pointer' 
                }}
              >
                Eliminar
              </button>
            </div>
            
            {isExisting ? (
              // Read-only display for existing activities
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>
                  <strong>Nivel:</strong> {activity.activity_level}
                </div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>
                  <strong>Detalles:</strong> {activity.details}
                </div>
              </div>
            ) : (
              // Editable fields for new activities
              <>
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Nombre:</span>
                  <input 
                    value={activity.name} 
                    onChange={e => handleActivityChange(index, 'name', e.target.value)}
                    style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }} 
                  />
                </label>
                
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Nivel de actividad:</span>
                  <select 
                    value={activity.activity_level} 
                    onChange={e => handleActivityChange(index, 'activity_level', e.target.value)}
                    style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }}
                  >
                    <option value="">Seleccionar nivel</option>
                    <option value="low">Bajo</option>
                    <option value="medium">Medio</option>
                    <option value="high">Alto</option>
                  </select>
                </label>
                
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Detalles:</span>
                  <textarea 
                    value={activity.details} 
                    onChange={e => handleActivityChange(index, 'details', e.target.value)}
                    style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13, minHeight: 40, resize: 'vertical' }} 
                  />
                </label>
              </>
            )}
          </div>
        );
      })}
    </div>
  </>
);
};

export default FitnessItemEditForm; 